import React from "react";

import { StatusDetail } from "../../../common/components";
import { Badge } from "../../../common/components/core";
import { StatusEntry } from "../../../common/components/StatusDetail";
import { InvoiceStatus, InvoiceHistory } from "../../../types/invoice";
import { UserRole } from "../../../types/userCurrent";
import { buildUniqueStatusHistory, lastOfArray } from "../../../utils/common";

import InvoiceStatusBadge from "./InvoiceStatusBadge";

type InvoiceStatusDetailProps = {
    roles: UserRole[];
    invoiceHistory: InvoiceHistory;
    latestStatus: InvoiceStatus;
    isPaymentRejected?: boolean;
    hasPaymentAction?: boolean;
};

const InvoiceStatusDetail = ({
    roles,
    invoiceHistory,
    latestStatus,
    isPaymentRejected,
    hasPaymentAction,
}: InvoiceStatusDetailProps): JSX.Element => {
    return (
        <StatusDetail
            className="mt-1 fs-7 fw-normal float-end"
            statusEntries={toStatusEntries(invoiceHistory, isPaymentRejected)}
        >
            {isPaymentRejected ? (
                hasPaymentAction ? (
                    <Badge type="danger">Action Required</Badge>
                ) : (
                    <Badge type="info">Pending</Badge>
                )
            ) : (
                <InvoiceStatusBadge roles={roles} status={latestStatus} />
            )}
        </StatusDetail>
    );
};

const toStatusEntries = (
    invoiceHistory: InvoiceHistory,
    isPaymentRejected?: boolean,
): StatusEntry[] => {
    const historyItems = invoiceHistory.statusHistory;
    const entries = buildUniqueStatusHistory(historyItems).map(
        (history): StatusEntry => {
            switch (history.status) {
                case InvoiceStatus.CONTRACTOR_SUBMITTED:
                    return {
                        role: "Service Provider",
                        date: history.timestamp,
                        status: "Created",
                        badgeType: "success",
                    };
                case InvoiceStatus.CONTRACTOR_DISCARDED:
                    return {
                        role: "Service Provider",
                        date: history.timestamp,
                        status: "Discarded",
                        badgeType: "discarded",
                    };
                case InvoiceStatus.PO_APPROVED:
                    return {
                        role: "Project Officer",
                        date: history.timestamp,
                        status: "Approved",
                        badgeType: "success",
                    };
                case InvoiceStatus.PO_REJECTED:
                    return {
                        role: "Project Officer",
                        date: history.timestamp,
                        status: "Rejected",
                        badgeType: "danger",
                    };
                case InvoiceStatus.FINANCE_APPROVED:
                    return {
                        role: "Finance Department",
                        date: history.timestamp,
                        status: "Approved",
                        badgeType: "success",
                    };
                case InvoiceStatus.FINANCE_REJECTED:
                    return {
                        role: "Finance Department",
                        date: history.timestamp,
                        status: "Rejected",
                        badgeType: "danger",
                    };
                case InvoiceStatus.DELETED:
                    return {
                        role: "Service Provider",
                        date: history.timestamp,
                        status: "Discarded",
                        badgeType: "discarded",
                    };
            }
        },
    );

    let firstEntry: StatusEntry | null = null;
    switch (historyItems[0]?.status) {
        case InvoiceStatus.CONTRACTOR_SUBMITTED:
            firstEntry = {
                role: "Project Officer",
                date: null,
                status: "CTA",
                badgeType: null,
            };
            break;
        case InvoiceStatus.PO_APPROVED:
            firstEntry = {
                role: "Finance Department",
                date: null,
                status: "CTA",
                badgeType: null,
            };
            break;
        case InvoiceStatus.PO_REJECTED:
        case InvoiceStatus.FINANCE_REJECTED:
            firstEntry = {
                role: "Service Provider",
                date: null,
                status: "CTA",
                badgeType: null,
            };
            break;
        case InvoiceStatus.FINANCE_APPROVED:
            if (isPaymentRejected) {
                firstEntry = {
                    role: "Finance Department",
                    date: null,
                    status: "CTA",
                    badgeType: null,
                };
            }
            break;
    }

    return firstEntry ? [firstEntry].concat(entries) : entries;
};

// TODO: consider to remove
export const buildEntriesWithoutConsecutiveSameRole = (
    statusHistory: StatusEntry[],
): StatusEntry[] => {
    return statusHistory.reduce((acc: StatusEntry[], cur) => {
        const last = lastOfArray(acc);
        if (last && last.role !== cur.role) {
            acc.push(cur);
        }
        return acc;
    }, []);
};

export default InvoiceStatusDetail;
